import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { formatDateDDMM, getWeekdayShort } from "../../../constants";
import { fetchTeamRosterMap } from "../../../utils/fetchTeamRosterMap";
import ScheduleTable from "./ScheduleTable";
import ScheduleSelectMatchModal from "./ScheduleSelectMatchModal";
import TournamentScheduleMatchesModal from "./TournamentScheduleMatchesModal";
import useTournamentSocket from "../../../hooks/useTournamentSocket";
import useFetchStages from "../../../hooks/useFetchStages";
import PageLoader from "../../../components/PageLoader";
import StageTabsScroll from "../components/StageTabsScroll";
import EmptyData from "../../EmptyData";
import { openTournamentSettings } from "../../../utils/tournamentSettingsBus";
import { useTheme } from "../../../contexts/ThemeContext";
import { useIsWideViewport } from "../../../hooks/useIsWideViewport";

const TournamentSchedule = ({ tournament, is_authorized, hasGuestToken, searchChips = [] }) => {
    const { id } = useParams();
    const api = useAxios();
    const { compactTable } = useTheme();

    const [courts, setCourts] = useState([]);
    const [rounds, setRounds] = useState([]);

    // Team (grade 3) matches only carry the team's own name, not its player
    // roster - see fetchTeamRosterMap.js's own comment. Fetched once per
    // tournament, same reasoning/shape as TournamentGroups.js's own
    // teamRoster, so the "who's on this team" match-card menu can list players.
    const [teamRoster, setTeamRoster] = useState(new Map());
    useEffect(() => {
        if (tournament?.grade !== 3 || !tournament?.player_quantity) return;
        let cancelled = false;
        fetchTeamRosterMap(api, id, tournament.player_quantity).then(map => {
            if (!cancelled) setTeamRoster(map);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, tournament?.grade, tournament?.player_quantity]);
    // Starts true (not false) - once useFetchStages resolves, it commits
    // stages/activeStage/stagesLoading:false all in the same render, but
    // fetchSchedule (the only other place `loading` gets set) only runs
    // *after* that render commits, from a separate effect. Starting false
    // left a one-frame window where `loading || stagesLoading` was false
    // and courts/rounds were still [] - EmptyData's "No schedule created"
    // flashed on screen before the real fetch/loader took over.
    const [loading, setLoading] = useState(true);

    const [matchModalOpen, setMatchModalOpen] = useState(false);
    const [selectedCellData, setSelectedCellData] = useState(null);
    // Wide viewports dock the match-picker panel and the matches sheet on
    // the same right-edge slot (see ParticipantPickerPanel/BottomSheetPanel)
    // - only one can occupy it at a time, so the matches sheet below is
    // unmounted for as long as the picker is open there.
    const isWide = useIsWideViewport();
    // Compact mode always uses the agenda (list) layout now - no more
    // Список/Сітка toggle. Standard (non-compact) mode has no toggle either,
    // always the one grid it's always had.
    const scheduleView = "list";

    const [selectedScoreMatchId, setSelectedScoreMatchId] = useState(null);
    // Bumped (not just selectedScoreMatchId, which may already equal the
    // clicked match) on every match click so the matches sheet always
    // (re)expands, even for a re-click on the same match.
    const [expandSignal, setExpandSignal] = useState(0);
    // The matches sheet only exists in the DOM while actually open now (see
    // BottomSheetPanel.jsx's own comment on why the old permanent peek was
    // removed) - no cross-remount persistence needed here the way Grids/
    // Groups need it (this page never swaps out from under itself), so a
    // plain closed-by-default useState is enough.
    const [sheetOpen, setSheetOpen] = useState(false);
    // Closes the match picker (if open) same as its own onClose does - both
    // panels dock on the same right-edge slot, and since the picker no
    // longer auto-closes after a pick (it stays open across several
    // schedules in a row), it would otherwise keep hogging that slot
    // forever, leaving no way back to the matches sheet short of the
    // picker's own tiny X. Clicking any match now always wins the slot
    // back for it instead.
    // useCallback (here and on the handlers below passed into ScheduleTable
    // as props) - it's now React.memo'd, so a plain function const recreated
    // fresh on every TournamentSchedule render would silently defeat that:
    // memo's shallow prop comparison sees a "changed" function prop every
    // time regardless of whether anything relevant actually changed. Same
    // reasoning as TournamentGrids.js/TournamentGroups.js's own identical pass.
    const expandMatch = useCallback((matchId) => {
        setSelectedScoreMatchId(matchId);
        setExpandSignal(n => n + 1);
        setSheetOpen(true);
        setMatchModalOpen(false);
        setPickedCandidateMatch(null);
    }, []);
    const closeMatchesSheet = useCallback(() => setSheetOpen(false), []);
    const [unscheduledMatches, setUnscheduledMatches] = useState(null);
    const [unscheduledNextUrl, setUnscheduledNextUrl] = useState(null);
    // A candidate match picked from the still-open picker panel (see
    // ScheduleSelectMatchModal.js's own hasTarget branch) rather than from
    // a specific "+"-targeted cell - same "Drop here"/crosshair treatment
    // as pickedMatch's move-mechanic, just scheduling a NEW match instead
    // of moving an already-scheduled one. Lives here (not local to
    // ScheduleTable.js like pickedMatch) because the modal that sets it and
    // the table that reads/acts on it are siblings, both children of this
    // component.
    const [pickedCandidateMatch, setPickedCandidateMatch] = useState(null);

    // While a pick-and-place move is in progress (see ScheduleTable's
    // onPickChange), the persistent matches bottom sheet below is made
    // non-interactive (stays mounted/visible, no layout shift) - it's a
    // fixed, full-width strip pinned to the bottom of the viewport, and the
    // destination cell the user needs to tap to finish the move routinely
    // lands underneath it, swallowing the tap instead of reaching the table.
    const [isPickingMatch, setIsPickingMatch] = useState(false);

    const { stages, activeStage, setActiveStage, isLoading: stagesLoading, fetchStages } = useFetchStages(
        api, `/api/v1.0/tournaments/${id}/schedules/stages/`, "day"
    );

    const updateMatches = useCallback((updatedMatches) => {
        if (!updatedMatches?.length) return;
        const map = Object.fromEntries(updatedMatches.map(m => [m.id, m]));
        setRounds(prevRounds => prevRounds.map(round => {
            if (!round.match) return round;

            const upd = map[round.match.id];
            if (!upd) return round;            
            
            if (upd.sets && upd.sets.some(s => s.number === undefined) && round.match.sets && round.match.sets.length > upd.sets.length) {
                delete upd.sets;
            }

            let mergedSets = round.match.sets;
            if (upd.sets) {
                const setsMap = {};
                (round.match.sets || []).forEach((s, i) => { 
                    if (s) {
                        const num = s.number !== undefined ? s.number : i + 1;
                        setsMap[num] = s; 
                    }
                });
                upd.sets.forEach((s, i) => {
                    if (s) {
                        const num = s.number !== undefined ? s.number : i + 1;
                        setsMap[num] = { ...(setsMap[num] || {}), ...s };
                    }
                });
                const res = Object.values(setsMap).sort((a, b) => a.number - b.number);
                mergedSets = res.length > 0 ? res : upd.sets;
            }

            return {
                ...round,
                match: {
                    ...round.match,
                    ...upd,
                    sets: mergedSets
                }
            };
        }));
    }, []);

    useTournamentSocket(id, (payload) => {
        if (payload?.wake_up) {
            if (activeStage) fetchSchedule(activeStage);
            setUnscheduledMatches(null);
            return;
        }

        if (payload?.event === 'schedule_cells_updated' && payload.stage_id === activeStage) {
            setRounds(prevRounds => {
                let updated = [...prevRounds];
                payload.cells.forEach(cell => {
                    const idx = updated.findIndex(r => r.id === cell.id && r.court === cell.court);

                    if (idx !== -1) {
                        if (cell.match === null && cell.vacated_schedule_match_id) {
                            if (updated[idx].schedule_match_id !== cell.vacated_schedule_match_id) {
                                return;
                            }
                        }
                        updated[idx] = { ...updated[idx], match: cell.match, schedule_match_id: cell.schedule_match_id };
                    }
                });
                return updated;
            });
        }
        else if (payload?.event === 'schedule_updated') {
            if (activeStage) fetchSchedule(activeStage);
            setUnscheduledMatches(null);
        }
        if (activeStage && payload?.updated_matches) {
            updateMatches(payload.updated_matches);

            // A Bye/participant removal in Draws or Groups (Bracket.js/
            // BracketClassic.js/TournamentGroupTable.js's own "Delete")
            // broadcasts here too, as a plain updated_matches payload (no
            // wake_up/schedule_updated marker - those two above cover a
            // schedule-side change, this one's coming from the bracket/
            // group side instead) - clearing a Bye can make its match newly
            // eligible to schedule, but this panel's own "Виберіть матч"
            // list only ever refetches on wake_up/schedule_updated, so it
            // silently never picked up the newly-eligible match. Each
            // entry here only lists the fields that actually changed
            // (MatchGraphPropagator.get_response_payload, backend) - a slot
            // field among them (vs. a pure score/status update, which never
            // includes these) is what actually signals "this could affect
            // who's schedulable", so this stays a no-op on the far more
            // frequent score-only broadcasts a live match sends.
            const SLOT_FIELDS = ["x1", "x2", "player_1", "player_2", "player_3", "player_4", "team_1", "team_2"];
            if (payload.updated_matches.some(m => SLOT_FIELDS.some(f => f in m))) {
                setUnscheduledMatches(null);
            }
        }
    });

    useEffect(() => {
        fetchStages();
    }, []);

    useEffect(() => {
        if (activeStage) {
            fetchSchedule(activeStage);
        } else if (!stagesLoading && stages.length === 0) {
            // No schedule stages exist at all - fetchSchedule (the only
            // other place `loading` gets cleared) never runs in that case,
            // so without this the initial `loading:true` below would stay
            // stuck forever instead of settling on the "No schedule
            // created" empty state.
            setLoading(false);
        }
    }, [activeStage, stagesLoading, stages.length]);

    useEffect(() => {
        const stageParam = new URLSearchParams(window.location.search).get("stage");
        const isValidParam = stageParam && stages.some(s => s.id === stageParam);

        if (isValidParam) {
            // Если в URL есть валидный stage, оставляем его
            setActiveStage(stageParam);
        } else if (stages.length > 0) {
            // Если в URL пусто (или указан удаленный stage), ищем сегодняшний день
            const d = new Date();
            // Формируем сегодняшнюю дату в формате YYYY-MM-DD с учетом таймзоны юзера
            const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            // Ищем стадию, чья дата начинается с сегодняшнего дня
            const todayStage = stages.find(s => s.date && s.date.substring(0, 10) === todayStr);

            if (todayStage) {
                setActiveStage(todayStage.id);
                // Сразу фиксируем в URL, чтобы при обновлении страницы или репосте ссылки она вела на этот же день
                const params = new URLSearchParams(window.location.search);
                params.set("stage", todayStage.id);
                window.history.replaceState({}, "", `?${params.toString()}`);
            }
        }
    }, [stages]);

    const latestStageRef = useRef(activeStage);
    useEffect(() => {
        latestStageRef.current = activeStage;
    }, [activeStage]);

    // Guards against out-of-order responses between *separate* fetchSchedule
    // calls for the *same* stage - latestStageRef alone only catches a stale
    // response from a stage the user has since switched away from, not two
    // in-flight requests for the current stage resolving out of order. That
    // does happen here: scheduling/deleting/moving a match each fire their
    // own fetchSchedule (rollback or resync), the backend's own websocket
    // wake_up (broadcast to every connected client, including whoever just
    // made the change) fires yet another, and nothing cancels an earlier one
    // before the next starts - so a slower, older response landing last
    // could silently overwrite fresher rounds/courts state with stale data
    // (a just-deleted match "reappearing", then vanishing again once a
    // later response finally lands). Same fix as groupsReqRef/drawsReqRef
    // in the Groups/Grids tabs.
    const scheduleReqRef = useRef(0);

    // silent=true skips the loading flag - used to refresh data behind an
    // already-mounted table (e.g. after scheduling a match) so the table
    // isn't unmounted/replaced by the PageLoader, which was causing a visible
    // "reload"/jump and losing the user's scroll position.
    // useCallback - referenced from inside several of the other stabilized
    // handlers below (onDeleteMatch/onMoveMatch/onSwapMatches' own error
    // rollback), which need it to be a stable reference for their own deps
    // arrays to mean anything.
    const fetchSchedule = useCallback(async (stageId, silent = false) => {
        if (!silent) setLoading(true);
        scheduleReqRef.current += 1;
        const currentReq = scheduleReqRef.current;
        try {
            const res = await api.get(`/api/v1.0/tournaments/${id}/schedules/stages/${stageId}/`);
            if (latestStageRef.current === stageId && scheduleReqRef.current === currentReq) {
                setCourts(res.data.courts || []);
                setRounds(res.data.rounds || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (!silent && latestStageRef.current === stageId) {
                setLoading(false);
            }
        }
    }, [api, id]);

    // Tapping the already-picked candidate match again cancels the pick,
    // same "tap source again to cancel" idea ScheduleTable's own
    // handleCellTap already uses for pickedMatch.
    const handlePickCandidateMatch = useCallback((match) => {
        setPickedCandidateMatch(prev => (prev && String(prev.id) === String(match.id)) ? null : match);
    }, []);

    // The reverse flow: a candidate match was picked from the still-open
    // panel (no specific cell targeted yet - see pickedCandidateMatch
    // above) and the user just tapped an empty cell to place it there.
    // Mirrors onMoveMatch below (optimistic pool removal + POST, full
    // refetch on success/failure) rather than a targeted optimistic
    // `setRounds` update - the new match's own schedule_match_id isn't
    // known client-side until the server assigns it, same reason the
    // modal's own Way-A success path (onSuccess above) already just
    // refetches instead of patching rounds in place.
    const onScheduleNewMatch = useCallback(async (candidateMatch, roundId, courtId) => {
        try {
            await api.post(`/api/v1.0/tournaments/${id}/schedules/stages/${activeStage}/match/`, {
                court: courtId,
                round: roundId,
                match: candidateMatch.id
            });
            setUnscheduledMatches(prev => prev ? prev.filter(m => m.id !== candidateMatch.id) : prev);
            fetchSchedule(activeStage, true);
            setPickedCandidateMatch(null);
        } catch (e) {
            console.error("Failed to schedule match:", e);
        }
    }, [api, id, activeStage, fetchSchedule]);

    const handleStageChange = useCallback((stageId) => {
        setActiveStage(stageId);
        const params = new URLSearchParams(window.location.search);
        params.set("stage", stageId);
        window.history.replaceState({}, "", `?${params.toString()}`);
    }, [setActiveStage]);

    // Stable (useCallback) versions of what used to be inline arrow props on
    // ScheduleTable below - it's now React.memo'd, and a fresh inline
    // `() => ...` on every render defeats that the same way a plain
    // function const would (see fetchSchedule's own comment above).
    const handleCancelCandidateMatch = useCallback(() => setPickedCandidateMatch(null), []);

    const handleOpenMatchModal = useCallback((roundId, courtId, scheduleMatchId) => {
        setSelectedCellData({ roundId, courtId, scheduleMatchId });
        setMatchModalOpen(true);
    }, []);

    // Clicking the exact "+ Match" cell that's already the open panel's own
    // target used to just re-open it with the same target again - no
    // visible change, which read as "clicking it does nothing, I can't
    // close this". Toggling it closed instead (same target clicked a
    // second time = cancel) gives that click a real effect, and matches the
    // panel's own close X. Reused below as ScheduleSelectMatchModal's own
    // onClose too - closing the panel any other way should cancel a
    // still-unplaced pick the same way.
    const handleCancelPickerTarget = useCallback(() => {
        setMatchModalOpen(false);
        setPickedCandidateMatch(null);
    }, []);

    const handleDeleteMatch = useCallback(async (scheduleMatchId, freedMatch) => {
        // Invalidates any fetchSchedule still in flight from an *earlier*
        // action (its own resync, a websocket wake_up, etc) - without this,
        // that older response could still land after this delete's own
        // optimistic clear and resurrect the just-removed match right back
        // into its cell. Repeated add/delete in quick succession is exactly
        // when an earlier fetchSchedule is most likely to still be in
        // flight when the next action fires, which is why this only ever
        // showed up "after several deletions/additions".
        scheduleReqRef.current += 1;
        if (freedMatch) {
            setUnscheduledMatches(prev => prev ? [freedMatch, ...prev] : prev);
        }

        setRounds(prev => prev.map(r =>
            String(r.schedule_match_id) === String(scheduleMatchId)
                ? { ...r, match: null, schedule_match_id: null }
                : r
        ));
        try {
            await api.delete(`/api/v1.0/tournaments/${id}/schedules/stages/${activeStage}/match/${scheduleMatchId}/`);
        } catch (e) {
            console.error(e);
            // Delete failed server-side: the match is still actually
            // scheduled, so drop it back out of the "available" pool we
            // optimistically added it to above, or it'd show up twice —
            // once still in its cell (restored below) and once as pickable.
            if (freedMatch) {
                setUnscheduledMatches(prev => prev ? prev.filter(m => m.id !== freedMatch.id) : prev);
            }
            // silent: true - a plain fetchSchedule(activeStage) sets
            // `loading`, which swaps the whole table out for a full-page
            // PageLoader (see the `loading || stagesLoading` branch below)
            // - on a failed delete that read as the page reloading itself,
            // unlike every other optimistic action here which never
            // disrupts the view like that.
            fetchSchedule(activeStage, true);
        }
    }, [id, activeStage, api, fetchSchedule]);

    const handleMoveMatch = useCallback(async (scheduleMatchId, targetRoundId, targetCourtId) => {
        // See handleDeleteMatch's identical comment above.
        scheduleReqRef.current += 1;
        setRounds(prev => {
            const draggedSlot = prev.find(r => String(r.schedule_match_id) === String(scheduleMatchId));
            if (!draggedSlot) return prev;

            return prev.map(r => {
                if (String(r.id) === String(targetRoundId) && String(r.court) === String(targetCourtId)) {
                    return { ...r, match: draggedSlot.match, schedule_match_id: scheduleMatchId };
                }
                if (String(r.schedule_match_id) === String(scheduleMatchId)) {
                    return { ...r, match: null, schedule_match_id: null };
                }
                return r;
            });
        });
        try {
            await api.patch(`/api/v1.0/tournaments/${id}/schedules/stages/${activeStage}/match/${scheduleMatchId}/`, {
                round: targetRoundId,
                court: targetCourtId
            });
        } catch (e) {
            console.error(e);
            fetchSchedule(activeStage, true); // Откат при ошибке, без полноэкранного лоадера
        }
    }, [id, activeStage, api, fetchSchedule]);

    const handleSwapMatches = useCallback((srcMatchId, srcRoundId, srcCourtId, targetMatchId, targetRoundId, targetCourtId) => {
        // See handleDeleteMatch's identical comment above.
        scheduleReqRef.current += 1;
        setRounds(prev => {
            const matchA = prev.find(r => String(r.schedule_match_id) === String(srcMatchId))?.match;
            const matchB = prev.find(r => String(r.schedule_match_id) === String(targetMatchId))?.match;

            return prev.map(r => {
                if (String(r.id) === String(targetRoundId) && String(r.court) === String(targetCourtId)) {
                    return { ...r, match: matchA, schedule_match_id: srcMatchId };
                }
                if (String(r.id) === String(srcRoundId) && String(r.court) === String(srcCourtId)) {
                    return { ...r, match: matchB, schedule_match_id: targetMatchId };
                }
                return r;
            });
        });
        api.patch(`/api/v1.0/tournaments/${id}/schedules/stages/${activeStage}/match/${srcMatchId}/`, {
            round: targetRoundId,
            court: targetCourtId
        }).catch(e => { console.error(e); fetchSchedule(activeStage, true); });

        api.patch(`/api/v1.0/tournaments/${id}/schedules/stages/${activeStage}/match/${targetMatchId}/`, {
            round: srcRoundId,
            court: srcCourtId
        }).catch(e => { console.error(e); fetchSchedule(activeStage, true); });
    }, [id, activeStage, api, fetchSchedule]);

    const handleScheduleModalSuccess = useCallback(() => {
        fetchSchedule(activeStage, true);
        // Clears the just-consumed target (not the panel itself, which
        // stays open on wide) - the FIRST pick after a "+" click still
        // fills that exact cell instantly; every pick after that has no
        // target, which is what flips the modal's own candidate-click into
        // "just pick, wait for a cell tap" instead of posting immediately
        // (see pickedCandidateMatch above).
        setSelectedCellData(null);
    }, [fetchSchedule, activeStage]);

    return (
        <>
            {pickedCandidateMatch && (
                <div
                    onClick={handleCancelCandidateMatch}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {/* id lives on this outer wrapper (not ScheduleTable's own root) so
                the day tabs above are actually part of #schedule-block, same
                as draw-stages-block wraps TournamentDrawStages' own
                StageTabsScroll + Groups/Grids - the Screenshot button only
                captures this element's subtree, so a stage row left outside
                it (as it used to be, a sibling before this div) never made
                it into the captured image. */}
            <div id="schedule-block">
                {
                    stages.length > 0 && courts.length > 0 &&
                    <ul className="tabs" style={{ marginTop: "10px", marginBottom: "5px" }}>
                        <StageTabsScroll
                            stages={stages}
                            activeStage={activeStage}
                            onStageChange={handleStageChange}
                        />
                    </ul>
                }

                {loading || stagesLoading ? (
                    <center>
                        <PageLoader />
                    </center>
                ) : stages.length === 0 || courts.length === 0 ? (
                    <EmptyData
                        title="No schedule created"
                        text="The schedule has not been generated yet."
                        actionLabel="Go to settings"
                        onAction={is_authorized ? () => openTournamentSettings("schedule") : undefined}
                    />
                ) : (
                <div className="admin-wrapper schedule-admin-wrapper" style={{ marginTop: 0, paddingTop: "5px" }}>
                        <ScheduleTable
                            id={id}
                            courts={courts}
                            setCourts={setCourts}
                            rounds={rounds}
                            setRounds={setRounds}
                            tournament={tournament}
                            tournamentGrade={tournament?.grade}
                            searchChips={searchChips}
                            scheduleView={compactTable ? scheduleView : "grid"}
                            cancelPickSignal={expandSignal}
                            pickerTarget={isWide && matchModalOpen ? selectedCellData : null}
                            pickedCandidateMatch={pickedCandidateMatch}
                            onPlaceCandidateMatch={onScheduleNewMatch}
                            onCancelCandidateMatch={handleCancelCandidateMatch}
                            onOpenMatchModal={handleOpenMatchModal}
                            onCancelPickerTarget={handleCancelPickerTarget}
                            onOpenMatchScore={expandMatch}
                            onDeleteMatch={handleDeleteMatch}
                            onMoveMatch={handleMoveMatch}
                            onSwapMatches={handleSwapMatches}
                            is_authorized={is_authorized}
                            canEdit={tournament?.can_edit}
                            onPickChange={setIsPickingMatch}
                        />
                </div>
                )}
            </div>

            <ScheduleSelectMatchModal
                isOpen={matchModalOpen}
                // Closing used to leave a still-unplaced pick stuck on - the
                // panel was the only place to cancel it (tap the same
                // candidate again, or the small floating "Tap destination
                // cell" pill), so closing it lost that and every click on
                // the table underneath kept silently no-oping (see
                // ScheduleTable.js's own onCancelCandidateMatch). Closing
                // the panel now cancels the pick too, same as clicking
                // through to a table click already does - identical body to
                // handleCancelPickerTarget above, reused rather than duplicated.
                onClose={handleCancelPickerTarget}
                onSuccess={handleScheduleModalSuccess}
                tournamentId={id}
                stageId={activeStage}
                tournamentGrade={tournament?.grade}
                roundData={selectedCellData}
                unscheduledMatches={unscheduledMatches}
                setUnscheduledMatches={setUnscheduledMatches}
                nextUrl={unscheduledNextUrl}
                setNextUrl={setUnscheduledNextUrl}
                pickedCandidateMatch={pickedCandidateMatch}
                onPickCandidateMatch={handlePickCandidateMatch}
            />

            <TournamentScheduleMatchesModal
                isOpen={sheetOpen && rounds.some(r => r.match != null) && !(isWide && matchModalOpen)}
                onClose={closeMatchesSheet}
                interactive={!isPickingMatch}
                expandSignal={expandSignal}
                rounds={rounds}
                courts={courts}
                tournamentId={id}
                tournamentGrade={tournament?.grade}
                fetchSchedule={() => fetchSchedule(activeStage)}
                updateMatches={updateMatches}
                selectedMatchId={selectedScoreMatchId}
                setSelectedMatchId={setSelectedScoreMatchId}
                stages={stages}
                activeStage={activeStage}
                setActiveStage={setActiveStage}
                searchChips={searchChips}
                teamRoster={teamRoster}
                is_authorized={is_authorized || hasGuestToken}
            />
        </>
    );
};

export default TournamentSchedule;
